import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { ISSUE_SECTIONS } from "@/lib/constants";
import { PROVIDER_LABELS } from "@/lib/tiers";
import { newsletterTerms } from "@/lib/skin";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Newspaper, Plus, Loader2, Send, Download, Copy, Check, X, ArrowRight, Layers, CalendarDays,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function IssueBuilder() {
  const { config } = useAuth();
  const terms = newsletterTerms();
  const provider = PROVIDER_LABELS[config?.email_provider] || "your email tool";
  const [issues, setIssues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [available, setAvailable] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newIssue, setNewIssue] = useState({ title: "", publication_date: "" });
  const [exportOpen, setExportOpen] = useState(false);
  const [exportText, setExportText] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadIssues = useCallback(async () => {
    const { data } = await api.get("/issues");
    setIssues(data);
    return data;
  }, []);

  const loadDetail = useCallback(async (iid) => {
    const { data } = await api.get(`/issues/${iid}`);
    setSelected(data);
    const subs = await api.get("/submissions", { params: { submission_type: "sunday_news" } });
    setAvailable(subs.data.filter((s) => !s.issue_id && ["approved_production_review", "approved_production_review_edits", "production_review", "in_production"].includes(s.status)));
  }, []);

  useEffect(() => { loadIssues().then((d) => { if (d.length) loadDetail(d[0].id); }); }, [loadIssues, loadDetail]);

  const createIssue = async () => {
    if (!newIssue.title || !newIssue.publication_date) return toast.error("Title and date required");
    setBusy(true);
    try {
      const { data } = await api.post("/issues", newIssue);
      toast.success("Issue created");
      setCreateOpen(false);
      setNewIssue({ title: "", publication_date: "" });
      await loadIssues();
      loadDetail(data.id);
    } finally { setBusy(false); }
  };

  const addItem = async (sid, section) => {
    await api.post(`/issues/${selected.id}/add-item`, { submission_id: sid, section });
    toast.success("Added to issue");
    loadDetail(selected.id);
  };
  const removeItem = async (sid) => {
    await api.post(`/issues/${selected.id}/remove-item`, { submission_id: sid });
    loadDetail(selected.id);
  };
  const changeSection = async (sid, section) => { await addItem(sid, section); };

  const publish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/issues/${selected.id}/publish`);
      toast.success(`Published ${data.published_items} item(s)`);
      await loadIssues();
      loadDetail(selected.id);
    } finally { setBusy(false); }
  };

  const doExport = async () => {
    const { data } = await api.get(`/issues/${selected.id}/export`);
    setExportText(data.content);
    setExportOpen(true);
  };

  const itemsBySection = (section) => (selected?.items || []).filter((i) => i.issue_section === section);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Newspaper className="h-5 w-5" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{terms.name} Issue Builder</h1>
            <p className="text-sm text-muted-foreground">Assemble approved items into a publishable issue.</p>
          </div>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-issue-btn"><Plus className="mr-1.5 h-4 w-4" /> New Issue</Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Issue list */}
        <Card className="p-3 lg:col-span-1">
          <p className="px-2 pb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">Issues</p>
          <div className="space-y-1" data-testid="issue-list">
            {issues.length === 0 && <p className="px-2 py-4 text-sm text-muted-foreground">No issues yet.</p>}
            {issues.map((iss) => (
              <button key={iss.id} onClick={() => loadDetail(iss.id)} data-testid={`issue-${iss.id}`}
                className={`w-full rounded-lg p-3 text-left transition-colors ${selected?.id === iss.id ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                <p className="truncate text-sm font-semibold">{iss.title}</p>
                <p className={`text-xs ${selected?.id === iss.id ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {iss.publication_date} {iss.status === "published" ? "· Published" : ""}
                </p>
              </button>
            ))}
          </div>
        </Card>

        {/* Builder */}
        <div className="space-y-6 lg:col-span-3">
          {!selected ? (
            <Card className="flex flex-col items-center gap-2 p-16 text-center text-muted-foreground">
              <Layers className="h-8 w-8" /><p>Create or select an issue to start building.</p>
            </Card>
          ) : (
            <>
              <Card className="flex flex-wrap items-center justify-between gap-3 p-5">
                <div>
                  <h2 className="font-heading text-xl font-semibold">{selected.title}</h2>
                  <p className="flex items-center gap-1 text-sm text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" /> {selected.publication_date} · {selected.items?.length || 0} item(s)</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={doExport} data-testid="export-issue-btn"><Download className="mr-1.5 h-4 w-4" /> Export</Button>
                  <Button onClick={publish} disabled={busy || selected.status === "published"} data-testid="publish-issue-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> {selected.status === "published" ? "Published" : "Publish Issue"}</>}
                  </Button>
                </div>
              </Card>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Available items */}
                <Card className="p-5">
                  <h3 className="font-heading font-semibold">Approved items to add</h3>
                  <div className="mt-3 space-y-2" data-testid="available-items">
                    {available.length === 0 && <p className="py-4 text-sm text-muted-foreground">No approved {terms.name} items available. Approve items first.</p>}
                    {available.map((s) => (
                      <div key={s.id} className="flex items-center gap-2 rounded-lg border p-3" data-testid={`available-${s.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{s.data?.title || "Untitled"}</p>
                          <p className="text-xs text-muted-foreground">{s.committee || "—"}</p>
                        </div>
                        <Button size="sm" variant="secondary" onClick={() => addItem(s.id, "Chapter Business")} data-testid={`add-${s.id}`}>
                          Add <ArrowRight className="ml-1 h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Issue sections */}
                <Card className="p-5">
                  <h3 className="font-heading font-semibold">Issue layout</h3>
                  <div className="mt-3 space-y-4 max-h-[560px] overflow-y-auto cc-scroll pr-1">
                    {ISSUE_SECTIONS.map((section) => {
                      const items = itemsBySection(section);
                      if (items.length === 0) return null;
                      return (
                        <div key={section} data-testid={`section-${section.replace(/\s+/g, "-")}`}>
                          <p className="text-xs font-bold uppercase tracking-wide text-primary">{section}</p>
                          <div className="mt-1.5 space-y-1.5">
                            {items.map((it) => (
                              <div key={it.id} className="flex items-center gap-2 rounded-lg border bg-secondary/40 p-2.5" data-testid={`issue-item-${it.id}`}>
                                <span className="min-w-0 flex-1 truncate text-sm">{it.data?.title || "Untitled"}</span>
                                <Select value={it.issue_section} onValueChange={(v) => changeSection(it.id, v)}>
                                  <SelectTrigger className="h-7 w-36 text-xs" data-testid={`section-select-${it.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{ISSUE_SECTIONS.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                                </Select>
                                <button onClick={() => removeItem(it.id)} className="rounded p-1 hover:bg-background" data-testid={`remove-${it.id}`}><X className="h-3.5 w-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {(selected.items || []).length === 0 && <p className="py-4 text-sm text-muted-foreground">No items added yet. Add approved items from the left.</p>}
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create issue dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-issue-dialog">
          <DialogHeader><DialogTitle className="font-heading">Create {terms.name} Issue</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Issue title</Label><Input className="mt-1.5" data-testid="issue-title-input" value={newIssue.title} onChange={(e) => setNewIssue({ ...newIssue, title: e.target.value })} placeholder="e.g. Sunday News — June 15" /></div>
            <div><Label>Publication date</Label><Input type="date" className="mt-1.5" data-testid="issue-date-input" value={newIssue.publication_date} onChange={(e) => setNewIssue({ ...newIssue, publication_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={createIssue} disabled={busy} data-testid="create-issue-confirm">Create Issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-2xl" data-testid="export-dialog">
          <DialogHeader><DialogTitle className="font-heading">Export issue content</DialogTitle></DialogHeader>
          <p className="-mt-1 text-sm text-muted-foreground" data-testid="export-provider-note">
            Formatted to paste into <span className="font-semibold text-foreground">{provider}</span>. Change your email platform in Settings.
          </p>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border bg-secondary/50 p-4 text-sm cc-scroll">{exportText}</pre>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(exportText); setCopied(true); setTimeout(() => setCopied(false), 1500); }} data-testid="copy-export-btn">
              {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />} Copy to clipboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
